import { ColorScale, definePreset } from "@openng/optimus-ui-themes";
import Aura from "@openng/optimus-ui-themes/aura";
import { AuraBaseDesignTokens } from "@openng/optimus-ui-themes/aura/base";
import { Preset } from "@openng/optimus-ui-themes/types";
interface MyDesignTokens extends AuraBaseDesignTokens {
  primitive?: AuraBaseDesignTokens["primitive"] & {
    greenTransparent: ColorScale;
    grayTransparent: ColorScale;
  };
  semantic?: AuraBaseDesignTokens["semantic"] & {
    extend?: {
      pageBackground: string;
      widgetBackground: string;
      widgetGapSize: string;
      tableRowHeight: string;
      hierarchyWidth: string;
      fontSizeXs: string;
      fontSizeSm: string;
      fontSizeM: string;
      fontSizeL: string;
      fontSizeXl: string;
      fontSizeXxl: string;
      iconSizeXs: string;
      iconSizeSm: string;
      iconSizeM: string;
      iconSizeL: string;
      iconSizeXl: string;
      iconSizeXxl: string;
      warning: ColorScale;
      critical: ColorScale;
      minor: ColorScale;
    };
  };
}
export const BotevoDesignTokens: Preset<MyDesignTokens> = {
  primitive: {
    borderRadius: { lg: "10px" },
    gray: {
      0: "#ffffff",
      100: "#ebebeb",
      200: "#c1c1c1",
      300: "#9a9a9a",
      400: "#939393",
      500: "#565656",
      600: "#4e4e4e",
      700: "#404040",
      800: "#252525",
      900: "#292929",
      950: "#191919",
    },
    grayTransparent: {
      0: "#ffffffa9",
      100: "#ebebeba9",
      200: "#c1c1c1a9",
      300: "#9a9a9aa9",
      400: "#939393a9",
      500: "#565656a9",
      600: "#4e4e4ea9",
      700: "#404040a9",
      800: "#252525a9",
      900: "#292929a9",
      950: "#191919a9",
    },
    greenTransparent: {
      50: "#eaf8e440",
      100: "#d5f1ca40",
      200: "#ace49640",
      300: "#59c92e30",
      400: "#59c92e40",
      500: "#47a02440",
      600: "#35781b40",
      700: "#23501240",
      800: "#11280940",
      900: "#08140440",
    },

    green: {
      50: "#eaf8e4",
      100: "#d5f1ca",
      200: "#ace496",
      300: "#82d662",
      400: "#59c92e",
      500: "#47a024",
      600: "#35781b",
      700: "#235012",
      800: "#112809",
      900: "#081404",
    },
    yellow: {
      50: "#fcf6e5",
      100: "#faedcb",
      200: "#f4da97",
      300: "#efc863",
      400: "#eab62f",
      500: "#c39827",
      600: "#9c791f",
      700: "#755b18",
      800: "#4e3d10",
      900: "#271e08",
    },
    red: {
      50: "#ffece8",
      100: "#ffd8d1",
      200: "#ffb1a3",
      300: "#ff8a75",
      400: "#ff6347",
      500: "#d5533b",
      600: "#aa422f",
      700: "#803224",
      800: "#552118",
      900: "#2b110c",
    },
    blue: {
      50: "#e1e1fb",
      100: "#c3c2f7",
      200: "#0077ff",
      300: "#4443e9",
      400: "#120ee2",
      500: "#0f0bbc",
      600: "#0c0996",
      700: "#090771",
      800: "#06054b",
      900: "#030226",
    },
  },
  semantic: {
    primary: {
      50: "{green.50}",
      100: "{green.100}",
      200: "{green.200}",
      300: "{green.300}",
      400: "{green.400}",
      500: "{green.500}",
      600: "{green.600}",
      700: "{green.700}",
      800: "{green.800}",
      900: "{green.900}",
    },
    extend: {
      pageBackground: "#000",
      widgetBackground: "{gray.900}",
      widgetGapSize: "clamp(1rem, 1.2vw, 1.5rem)",
      tableRowHeight: "50px",
      hierarchyWidth: "clamp(9.3rem, 17vw, 17.5rem)",
      fontSizeXs: "clamp(0.48rem, 0.6vw, 0.75rem)",
      fontSizeSm: "clamp(0.6rem, 0.8vw, 0.8rem)",
      fontSizeM: "clamp(0.7rem, 1vw, 1rem)",
      fontSizeL: "clamp(1.2rem, 1.4vw, 1.8rem)",
      fontSizeXl: "clamp(1.5rem, 1.8vw, 2.2rem)",
      fontSizeXxl: "clamp( 1.3rem, 2.2vw, 2.5rem)",
      iconSizeXs: "clamp(0.82rem, 0.6vw, 0.9rem)",
      iconSizeSm: "clamp(1.1rem, 0.9vw, 1.3rem)",
      iconSizeM: "clamp(2.1rem, 1.8vw, 3.2rem)",
      iconSizeL: "clamp(3.2rem, 2.5vw, 4.8rem)",
      iconSizeXl: "clamp(4.1rem, 3vw, 5.6rem)",
      iconSizeXxl: "clamp(5.2rem, 4vw, 7rem)",
      warning: {
        50: "{yellow.50}",
        100: "{yellow.100}",
        200: "{yellow.200}",
        300: "{yellow.300}",
        400: "{yellow.400}",
        500: "{yellow.500}",
        600: "{yellow.600}",
        700: "{yellow.700}",
        800: "{yellow.800}",
        900: "{yellow.900}",
      },
      critical: {
        50: "{red.50}",
        100: "{red.100}",
        200: "{red.200}",
        300: "{red.300}",
        400: "{red.400}",
        500: "{red.500}",
        600: "{red.600}",
        700: "{red.700}",
        800: "{red.800}",
        900: "{red.900}",
      },
      minor: {
        50: "{blue.50}",
        100: "{blue.100}",
        200: "{blue.200}",
        300: "{blue.300}",
        400: "{blue.400}",
        500: "{blue.500}",
        600: "{blue.600}",
        700: "{blue.700}",
        800: "{blue.800}",
        900: "{blue.900}",
      },
    },

    colorScheme: {
      dark: {
        surface: {
          50: "{gray.50}",
          100: "{gray.100}",
          200: "{gray.200}",
          300: "{gray.300}",
          400: "{gray.400}",
          500: "{gray.500}",
          600: "{gray.600}",
          700: "{gray.700}",
          800: "{gray.800}",
          900: "{gray.900}",
          950: "{gray.950}",
        },
        formField: {
          background: "{gray.400}",
          borderColor: "{gray.100}",
          hoverBorderColor: "{primary.400}",
          placeholderColor: "{gray.0}",
          iconColor: "{gray.0}",
        },
      },
    },
  },
  components: {
    datatable: {
      colorScheme: {
        dark: {
          header: { borderColor: "{gray.100}" },
          headerCell: { borderColor: "{gray.100}" },
          bodyCell: { borderColor: "{gray.700}" },
        },
      },
    },
    toast: {
      colorScheme: {
        dark: {
          success: {
            background: "{primary.400}",
            color: "{gray.0}",
          },
          error: {
            background: "{critical.400}",
            color: "{gray.0}",
          },
          warn: {
            background: "{warning.400}",
            color: "{gray.0}",
          },
        },
      },
    },
    datepicker: {
      dropdown: {
        width: "1.5rem",
      },
      colorScheme: {
        dark: {
          today: {
            background: "{gray.400}",
          },
          date: {
            hoverBackground: "{greenTransparent.500}",
          },
        },
      },
    },
    confirmpopup: {
      colorScheme: {
        dark: { root: { background: "{gray.500}", arrowOffset: ".7rem" } },
      },
    },
    paginator: {
      colorScheme: { dark: { root: { background: "{gray.500}" } } },
    },
    select: {
      colorScheme: {
        dark: {
          root: { placeholderColor: "{gray.200}" },
          overlay: { background: "{gray.400}", borderRadius: "3px" },
          option: {
            focusBackground: "{primary.300}",
            selectedFocusBackground: "{primary.300}",
          },
        },
      },
    },
    progressspinner: {
      colorScheme: {
        dark: {
          root: {
            colorOne: "{primary.400}",
            colorTwo: "{primary.600}",
            colorThree: "{primary.400}",
            colorFour: "{primary.600}",
          },
        },
      },
    },
  },
};
export const BotevoPreset = definePreset(Aura, BotevoDesignTokens);
